import {
  IPresentationResponse,
  load,
  coverImage,
  create,
  destroy,
  list,
  update,
  upload,
} from "common/api/presentation.service";
import { DANGER, SUCCESS, WARNING, addFlashAlert } from "common/components/FlashAlert";
import {
  IPresentationData,
  IPresentationOptions,
  PresentationRecord,
  presentationFromJSON,
} from "common/records/Presentation";
import { RootState, ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { PresentationActions } from "common/reducers/presentation.actions";
import { currentJobId } from "common/selectors/job.selectors";
import { currentOrgId } from "common/selectors/org.selectors";
import { presentation, presentationsSelectedIds } from "common/selectors/presentation.selectors";
import { RootDispatchType, RootGetState } from "common/store";
import { FileWithPreview, JOB_URL, Nullable, ORG_URL, UrlType } from "common/types";
import { List } from "immutable";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { dirtyIds } from "common/selectors/idsManagement.selectors";
import { TaskActions } from "common/reducers/task.actions";
import { fileFromJSON } from "common/records/File";
import { FileRecord, getBase64 } from "common/records/File";
import { isUndefined } from "underscore";
import { PresentationParamsRecord } from "common/records/index/PresentationParams";
import { ITaskData } from "common/records/Task";
import { watchCurrentJob } from "src/router/loaders/helper";
import { EstimateAsyncActions } from "common/reducers/estimate.async.actions";

const modelName = "presentations";

export const PresentationAsyncActions = {
  getPresentation: (id: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(PresentationActions.fetchPresentation(id));

      try {
        const response = await load(id);
        dispatch(PresentationActions.receivePresentation(response.presentation));
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationLoaded(id));
      }
    };
  },
  listPresentations: (queryParams: PresentationParamsRecord): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      if (queryParams.get("parentId") === 0 || isUndefined(queryParams.get("parentId"))) {
        queryParams = queryParams.set("parentId", getParentId(queryParams.urlType, getState()));
      }

      dispatch(IdsManagementActions.fetchModel(modelName, queryParams));

      try {
        const response = await list(queryParams);
        dispatch(PresentationActions.receivePresentations(response.presentations));

        const ids = List(response.presentations.map((presentation: IPresentationData) => presentation.id));

        if (queryParams.urlType === JOB_URL) {
          const job = await watchCurrentJob();
          if (!job) return;

          const inspectionId = job?.inspections?.first()?.id || 0;

          if (inspectionId > 0) {
            dispatch(EstimateAsyncActions.getEstimate(job.id, inspectionId, false));
          }
          if (response.presentations.length === 0) {
            dispatch(PresentationActions.initializeJobPresentation(job?.id, inspectionId));
            dispatch(IdsManagementActions.receiveModel("jobPresentations", List([-1])));
          } else {
            dispatch(IdsManagementActions.receiveModel("jobPresentations", ids));
          }
        } else {
          dispatch(IdsManagementActions.receiveModel(modelName, ids, response?.meta?.pagination));
        }
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      }
    };
  },
  batchCreatePresentation: (files: FileWithPreview[], options: IPresentationOptions): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      let presentationSuccess = 0;
      if (files.length === 0) {
        addFlashAlert("danger", "No files found");
        return;
      }

      dispatch(PresentationActions.setLoading(true));

      await Promise.all(
        files.map(async (file) => {
          const record = presentationFromJSON({
            name: file.name,
            kind: options.kind,
            data: file,
            org_id: currentOrgId(getState()),
            folder_id: options.folderId,
          });
          try {
            const response = await upload(record);

            dispatch(PresentationActions.receivePresentation(response.presentation));
            dispatch(PresentationAsyncActions.processPresentation(response.presentation));
            presentationSuccess += 1;
          } catch (response) {
            console.error(response);
          }
        }),
      );

      let flashLevel = WARNING;
      if (presentationSuccess === files.length) {
        flashLevel = SUCCESS;
      }

      dispatch(PresentationActions.setLoading(false));
      addFlashAlert(flashLevel, presentationSuccess + "/" + files.length + " Presentations successfully uploaded");
    };
  },
  createPresentation: (presentation: Nullable<PresentationRecord>, urlType: UrlType): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      if (!presentation) {
        addFlashAlert("danger", "No presentation found");
        return;
      }
      const parentId = getParentId(urlType, getState());
      try {
        dispatch(PresentationActions.fetchPresentation(presentation.id));
        const response = await create(parentId, presentation, urlType);

        dispatch(PresentationActions.receivePresentation(response.presentation));
        dispatch(PresentationAsyncActions.processPresentation(response.presentation));

        if (presentation.cover_image instanceof File) {
          await dispatch(PresentationAsyncActions.createCoverImage(presentation.cover_image, response.presentation.id));
        }

        dispatch(IdsManagementActions.pushId(response.presentation.id, modelName));
        dispatch(PresentationActions.setPresentationLoaded(presentation.id));
        dispatch(PresentationActions.removePresentation(presentation.id));

        addFlashAlert("success", "Presentation successfully created");
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationLoaded(presentation.id));
      }
    };
  },
  updatePresentation: (presentation: Nullable<PresentationRecord>): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      if (!presentation) {
        addFlashAlert("danger", "No presentation found");
        return;
      }
      dispatch(PresentationActions.fetchPresentation(presentation.id));

      try {
        const response = await update(presentation);
        dispatch(PresentationActions.receivePresentation(response.presentation));
        if (presentation.cover_image instanceof File) {
          await dispatch(PresentationAsyncActions.createCoverImage(presentation.cover_image, response.presentation.id));
        }
        addFlashAlert("success", "Presentation successfully updated");
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationLoaded(presentation.id));
      }
    };
  },
  createCoverImage: (file: File, presentationId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      let record = presentation(getState(), { presentationId });
      if (!record) {
        addFlashAlert("danger", "No presentation found");
        return;
      }

      try {
        const result = await getBase64(file);
        record = record.set("cover_image", result as FileRecord);
        dispatch(PresentationActions.fetchPresentation(presentationId));

        const response = await coverImage(record);
        dispatch(PresentationActions.receivePresentation(response.presentation));
        addFlashAlert("success", "Cover image successfully added");
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationLoaded(presentationId));
      }
    };
  },
  destroyPresentation: (presentation: Nullable<PresentationRecord>): ThunkResult<Promise<Nullable<void>>> => {
    return async (dispatch: RootDispatchType) => {
      if (!presentation) {
        addFlashAlert("danger", "No presentation found");
        return;
      }
      dispatch(PresentationActions.fetchPresentation(presentation.id));

      try {
        await destroy(presentation);
        dispatch(PresentationActions.removePresentation(presentation.id));

        addFlashAlert("success", "Presentation successfully deleted");
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationLoaded(presentation.id));
      }
    };
  },
  batchUpdatePresentation: (presentationIds: List<number>, reject = false): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const promises: Promise<IPresentationResponse>[] = [];
      dispatch(PresentationActions.fetchPresentations(presentationIds));

      try {
        presentationIds.forEach((presentationId) => {
          const updatedPresentation = presentation(getState(), { presentationId });

          if (updatedPresentation) {
            promises.push(update(updatedPresentation));
          }
        });
        const responses = await Promise.all(promises);
        const presentations = responses.map((response) => response.presentation);
        dispatch(PresentationActions.receivePresentations(presentations));
        addFlashAlert("success", "Saved presentations successfully");
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationsLoaded(presentationIds));
        if (reject) {
          throw response;
        }
      }
    };
  },
  processPresentation: (presentation: IPresentationData): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.pushId(presentation.id, modelName));

      const { task } = presentation;
      if (task) {
        const context = {
          model: "presentation",
          action: "processPresentationTaskHandler",
          action_payload: { id: presentation.id },
          error_action: "errorTaskHandler",
        };
        task.context = context;
        dispatch(TaskActions.receiveTask(task));
      }
    };
  },
  processPresentationTaskHandler: (task: ITaskData, payload: { id: number }): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      if (task.results.cover_image) {
        dispatch(
          PresentationActions.editPresentation(payload.id, "cover_image", fileFromJSON(task.results.cover_image)),
        );
      }
    };
  },
  onSortEnd: (oldIndex: number, newIndex: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(IdsManagementActions.onSortEnd(modelName, oldIndex, newIndex));

      const ids = dirtyIds(getState()) as List<number>;
      if (!ids || ids.size === 0) {
        return;
      }
      await dispatch(PresentationAsyncActions.batchUpdatePresentation(ids));

      dispatch(IdsManagementActions.setDirtyIds(null));
    };
  },
  movePresentations: (folderId: number): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const presentationIds = presentationsSelectedIds(getState(), { modelName }) as List<number>;
      const prevFolderId = presentation(getState(), { presentationId: presentationIds.first() })?.folder_id;

      dispatch(PresentationActions.editPresentations(presentationIds, "folder_id", folderId));
      try {
        await dispatch(PresentationAsyncActions.batchUpdatePresentation(presentationIds, true));

        presentationIds.forEach((presentationId) => {
          dispatch(IdsManagementActions.removeId(presentationId, modelName));
        });
      } catch {
        dispatch(PresentationActions.editPresentations(presentationIds, "folder_id", prevFolderId));
      }
    };
  },
  errorTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      const errorMessage =
        task?.results?.error_message ||
        "There were problems processing your task. Please try again. If the problem persists contact support.";

      addFlashAlert(DANGER, errorMessage);
    };
  },
  addJobPresentation: (presentation: PresentationRecord): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      try {
        const jobId = currentJobId(getState());
        if (!jobId) {
          addFlashAlert("danger", "No job found");
          return;
        }
        dispatch(IdsManagementActions.fetchModel("jobPresentations"));

        const response = await create(jobId, presentation, "job");

        dispatch(PresentationActions.receivePresentation(response.presentation));
        dispatch(IdsManagementActions.receiveModel("jobPresentations", List([response.presentation.id])));
        addFlashAlert(SUCCESS, "Dynamic Presentation created successfully");
      } catch (response) {
        handleErrors(response);
        dispatch(PresentationActions.setPresentationLoaded(presentation.id));
      }
    };
  },
};

export const getParentId = (urlType: UrlType, state: RootState): number => {
  let parentId = 0;
  switch (urlType) {
    case JOB_URL:
      const jobId = currentJobId(state);
      if (jobId) {
        parentId = jobId;
      }
      break;
    case ORG_URL:
      const orgId = currentOrgId(state);
      if (orgId) {
        parentId = orgId;
      }
      break;
    default:
  }

  return parentId;
};
